import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Download, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/tools/before-after")({
  head: () => ({
    meta: [
      { title: "Before & After Image Combiner | Apex AV" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BeforeAfterPage,
});

type Layout = "side" | "stacked" | "split";

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image"));
    };
    img.src = url;
  });
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const scale = Math.max(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
}

function drawLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  bottom: number,
) {
  if (!text) return;
  ctx.font = "600 34px ui-sans-serif, system-ui, sans-serif";
  const padX = 22;
  const padY = 12;
  const m = ctx.measureText(text);
  const w = m.width + padX * 2;
  const h = 34 + padY * 2;
  const x = cx - w / 2;
  const y = bottom - h - 24;
  ctx.fillStyle = "rgba(10, 12, 24, 0.72)";
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, 999);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, cx, y + h / 2 + 1);
}

function BeforeAfterPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [before, setBefore] = useState<HTMLImageElement | null>(null);
  const [after, setAfter] = useState<HTMLImageElement | null>(null);
  const [beforeLabel, setBeforeLabel] = useState("Before");
  const [afterLabel, setAfterLabel] = useState("After");
  const [layout, setLayout] = useState<Layout>("side");
  const [gap, setGap] = useState(16);
  const [split, setSplit] = useState(50);

  const pick = async (file: File | undefined, which: "before" | "after") => {
    if (!file) return;
    try {
      const img = await loadImage(file);
      if (which === "before") setBefore(img);
      else setAfter(img);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const CELL = 1200;
    let W = CELL;
    let H = CELL;
    if (layout === "side") {
      W = CELL * 2 + gap;
      H = CELL;
    } else if (layout === "stacked") {
      W = CELL;
      H = CELL * 2 + gap;
    }
    canvas.width = W;
    canvas.height = H;

    ctx.fillStyle = "#0b0d18";
    ctx.fillRect(0, 0, W, H);

    if (!before && !after) return;

    if (layout === "side") {
      if (before) drawCover(ctx, before, 0, 0, CELL, H);
      if (after) drawCover(ctx, after, CELL + gap, 0, CELL, H);
      drawLabel(ctx, beforeLabel, CELL / 2, H);
      drawLabel(ctx, afterLabel, CELL + gap + CELL / 2, H);
    } else if (layout === "stacked") {
      if (before) drawCover(ctx, before, 0, 0, W, CELL);
      if (after) drawCover(ctx, after, 0, CELL + gap, W, CELL);
      drawLabel(ctx, beforeLabel, W / 2, CELL);
      drawLabel(ctx, afterLabel, W / 2, H);
    } else {
      const x = Math.round((W * split) / 100);
      if (before) drawCover(ctx, before, 0, 0, W, H);
      if (after) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, 0, W - x, H);
        ctx.clip();
        drawCover(ctx, after, 0, 0, W, H);
        ctx.restore();
      }
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(x - 3, 0, 6, H);
      drawLabel(ctx, beforeLabel, Math.min(x / 2, W - 120), H);
      drawLabel(ctx, afterLabel, Math.max(x + (W - x) / 2, 120), H);
    }
  }, [before, after, beforeLabel, afterLabel, layout, gap, split]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas || (!before && !after)) {
      toast.error("Add at least one image first");
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `before-after-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const swap = () => {
    setBefore(after);
    setAfter(before);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold">Before &amp; After Combiner</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Combine two job photos into one shareable before/after image.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-4 rounded-lg border border-border bg-card p-4">
          {(["before", "after"] as const).map((which) => (
            <div key={which}>
              <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                {which} image
              </div>
              <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent">
                <Upload className="h-4 w-4" />
                {(which === "before" ? before : after) ? "Replace image" : "Choose image"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => pick(e.target.files?.[0], which)}
                />
              </label>
              <Input
                className="mt-2"
                value={which === "before" ? beforeLabel : afterLabel}
                onChange={(e) =>
                  which === "before"
                    ? setBeforeLabel(e.target.value)
                    : setAfterLabel(e.target.value)
                }
                placeholder="Label (leave blank to hide)"
              />
            </div>
          ))}

          <div>
            <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">Layout</div>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ["side", "Side by side"],
                  ["stacked", "Stacked"],
                  ["split", "Split slider"],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setLayout(value)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    layout === value
                      ? "bg-foreground text-background"
                      : "border-border hover:bg-accent"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {layout === "split" ? (
            <label className="block">
              <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                Split position ({split}%)
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={split}
                onChange={(e) => setSplit(Number(e.target.value))}
                className="w-full"
              />
            </label>
          ) : (
            <label className="block">
              <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                Divider gap ({gap}px)
              </div>
              <input
                type="range"
                min={0}
                max={80}
                value={gap}
                onChange={(e) => setGap(Number(e.target.value))}
                className="w-full"
              />
            </label>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={swap}>
              <ArrowLeftRight className="mr-2 h-4 w-4" /> Swap
            </Button>
            <Button size="sm" onClick={download}>
              <Download className="mr-2 h-4 w-4" /> Download PNG
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <canvas
            ref={canvasRef}
            className="mx-auto h-auto w-full max-w-3xl rounded-md"
          />
          {!before && !after && (
            <p className="mt-3 text-center text-sm text-muted-foreground">
              Add a before and after photo to build the comparison.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
